import { createContext, useCallback, useContext, useEffect, useState } from 'react';

const STORAGE_KEY = 'theme';

function getInitial(): boolean {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) return stored === 'dark';
  // Dark is the site's default, so a first visit gets it regardless of what
  // the OS asks for. Anyone who wants light can switch, and that choice is
  // stored and wins from then on.
  return true;
}

function apply(dark: boolean) {
  if (dark) {
    document.documentElement.setAttribute('data-theme', 'dark');
  } else {
    document.documentElement.removeAttribute('data-theme');
  }

  // Keep the mobile browser chrome with the page. Left at its static white,
  // a phone draws a white bar above a dark site.
  const themeColor = document.querySelector('meta[name="theme-color"]');
  if (themeColor) themeColor.setAttribute('content', dark ? '#1D1F1F' : '#FFFFFF');
}

interface ThemeContextValue {
  isDark: boolean;
  toggle: () => void;
}

export const ThemeContext = createContext<ThemeContextValue>({
  isDark: false,
  toggle: () => {},
});

export function useThemeProvider(): ThemeContextValue {
  const [isDark, setIsDark] = useState<boolean>(getInitial);

  useEffect(() => {
    apply(isDark);
    localStorage.setItem(STORAGE_KEY, isDark ? 'dark' : 'light');
  }, [isDark]);

  const toggle = useCallback(() => {
    document.documentElement.classList.add('theme-transitioning');
    setIsDark((prev) => !prev);
    setTimeout(() => document.documentElement.classList.remove('theme-transitioning'), 300);
  }, []);

  return { isDark, toggle };
}

export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext);
}
